import { useState, useRef, useEffect, useCallback } from "react";
import {
  Sparkles, Image as ImageIcon, Film, User, Bot, Wand2, Zap,
  Send, Paperclip, ChevronDown, X, Loader2, Settings2, Plus,
  ArrowUp, MessageSquarePlus, LayoutGrid, Frame as FrameIcon,
  Layers, PenTool, Clock, Camera, RotateCw, RefreshCw, Trash2,
  Crop, ZoomIn, RefreshCcw, Check, PinIcon, GripVertical, Music, VolumeX
} from "lucide-react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useNavigate } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { ToolChip } from "@/components/studio/atoms";
import { CameraPresetsMenu, CAMERA_PRESETS } from "@/components/studio/CameraPresetsMenu";
import { CameraAnglePresetsMenu, CAMERA_ANGLES } from "@/components/studio/CameraAnglePresetsMenu";
import { api } from "@/lib/api";
import { proxyMediaUrl } from "@/lib/media";
import { toast } from "sonner";

/**
 * ChatPromptDock — a fixed-bottom, chat-style composer for Geneo AI Studio.
 *
 * Layout:
 *   [MODE ▼] [Auto] [Skills] ─────────────── [Attach ⎘] [text area] [↑ send]
 *
 * Modes: AI Image, AI Video, AI Avatar, AI Agent, Nova Prompt Insert.
 *   AI Avatar    → routes to /avatar
 *   AI Agent     → opens Nova panel (broadcasts CustomEvent 'nova:open')
 *   Nova Prompt  → opens rewrite popover (fetches /prompt/rewrites, 3 options)
 *
 * `attachContent` (JSX) fills the Attach popover — pass refs/frames pickers here.
 * `skillsContent` (JSX) fills the Skills popover — pass style presets, aspect, etc.
 * `onSubmit({ prompt, mode })` is called on send; parent runs the actual generation.
 */

const MODE_OPTIONS = [
  { key: "image",  label: "AI Image",  desc: "Nano Banana · 1 credit",   icon: ImageIcon },
  { key: "video",  label: "AI Video",  desc: "Seedance · pick model",     icon: Film },
  { key: "avatar", label: "AI Avatar", desc: "Portrait lipsync · route", icon: User, route: "/avatar" },
  { key: "agent",  label: "AI Agent",  desc: "Chat with Nova · free",    icon: Bot, action: "openNova" },
  { key: "nova",   label: "Nova Prompt Insert", desc: "3 rewrites · free", icon: Wand2, action: "rewrites" },
];

/** Seedance models — 2.0 (standard, 3 credits) or 2.5 (premium 30s single-shot, flat 10 credits).
 *  2.5 offers native 30-second single-shot generation, coherent motion/lighting/identity, and
 *  synchronized audio. Priced at a flat premium per generation. */
export const SEEDANCE_MODELS = [
  { key: "2.0", label: "Seedance 2.0", desc: "Standard · 5-10s clips",                       flatCost: null },
  { key: "2.5", label: "Seedance 2.5", desc: "Native 30s single-shot · 720p · 10 credits",   flatCost: 10 },
];

/** Video-only submodes — control how references and frames are used
 *  for the Seedance 2.0 pipeline. Selecting one influences the params
 *  the parent forwards to /generate/text-video. */
export const VIDEO_SUBMODES = [
  { key: "omni",       label: "Omni Reference",       icon: LayoutGrid, desc: "Attach up to 6 reference images or videos to influence the whole shot",  beta: false },
  { key: "endpoints",  label: "First and Last Frames", icon: FrameIcon,  desc: "Pin the opening and closing frames — Seedance interpolates the arc",   beta: false },
  { key: "multiframe", label: "Multiframes",          icon: Layers,     desc: "Guide the shot with a sequence of keyframes (up to 4)",                 beta: false },
  { key: "smartedit",  label: "Smart Edit",           icon: PenTool,    desc: "AI-assisted video-to-video restyle with prompt-level control",         beta: true  },
  { key: "long",       label: "Long Video",           icon: Clock,      desc: "Generate longer sequences (10-15s) with cinematic pacing",             beta: true  },
];

/** Credit cost calc — Seedance 2.0 video defaults to 3 credits (+2 for Long submode).
 *  Seedance 2.5 is a flat 10 credits regardless of submode. Image is always 1. */
export function computeCreditCost(mode, submode, duration, videoModel = "2.0") {
  if (mode === "image")     return 1;
  if (mode === "turnaround")return 3;
  if (mode === "video" || mode === "v2v") {
    // Seedance 2.5 flat rate — matches backend `CREDIT_COSTS["video-25"]`/`["text-video-25"]`.
    // V2V stays on 2.0 so the flat override only applies when mode === "video".
    const model = SEEDANCE_MODELS.find((m) => m.key === videoModel) || SEEDANCE_MODELS[0];
    if (mode === "video" && model.flatCost) return model.flatCost;
    let cost = 3;
    if (submode === "long" || (duration && duration >= 10)) cost += 2;
    return cost;
  }
  return 0;
}

export function ChatPromptDock({
  mode = "image",
  setMode,
  prompt,
  setPrompt,
  onSubmit,
  attachContent = null,
  attachSummary = null,
  skillsContent = null,
  disabled = false,
  auto,
  setAuto,
  placeholderByMode,
  compact = false,
  // Video-only extras (safe to omit for other modes)
  videoSubmode = "omni",
  setVideoSubmode,
  videoModel = "2.0",
  setVideoModel,
  // Video-only: when false, suppress background music in the generated clip
  // while preserving SFX/ambient (prompt-conditioned on Seedance 2.5).
  musicEnabled = true,
  setMusicEnabled,
  aspect,
  setAspect,
  videoAspects = ["16:9", "9:16", "1:1"],
  duration,
  setDuration,
  durations = [5, 10],
  // Optional: lift camera-angle/camera-move selection to the parent status pill
  onAngleChange,
  onCameraChange,
  // Feed-item drag/drop → attach handler (fires when user drops a completed render onto the paperclip)
  onFeedDrop,
  // Left-side thumbnail rail (primary upload zone) — refs are `{url, uploaded_url, kind}[]`
  // rendered as square cards on the left of the textarea. `onAddRef(file)` is called for
  // both click-to-upload (via the +) and drag-drop onto the rail. Zero refs shows just a
  // single + card. The paperclip pill in the toolbar remains as a secondary trigger.
  refs = [],
  onAddRef,
  onRemoveRef,
  // Reorder support (swap two slots) and per-slot replace (drop a fresh file
  // onto an occupied thumb → replace that slot). Both are optional — the
  // rail degrades to append-only if the parent doesn't wire them.
  onReorderRefs,
  onReplaceRef,
  refsCap = 5,
  // Optional slot rendered in the toolbar's right group, immediately before
  // the credit-cost/GENERATE chip. Used by Studio to inline the queue status
  // pill so it lives on the same row as AI IMAGE / ratio / CAMERA ANGLE /
  // AUTO instead of floating separately above the dock.
  queueSlot = null,
}) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const railFileRef = useRef(null);

  // Publish the dock's live height as a CSS custom property on <html> so any
  // parent scroll container can reserve exactly the right bottom padding —
  // "feed cards must never sit under the composer". Recomputes on window
  // resize AND on any internal size change (expanding style panel, mode
  // switch, reference rail growing). See Studio.jsx pb-[calc(...)] usage.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const setVar = () => {
      const rect = el.getBoundingClientRect();
      // Dock is anchored at `bottom-3` / `bottom-6` so its total footprint
      // from the viewport bottom is height + that offset. Read the bottom
      // gap from the rendered rect so we don't drift if a caller overrides it.
      const gap = Math.max(0, window.innerHeight - rect.bottom);
      root.style.setProperty("--geneo-dock-h", `${Math.ceil(rect.height + gap)}px`);
    };
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    window.addEventListener("resize", setVar);
    setVar();
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
      // Clear so pages that don't mount the dock (e.g. Gallery) don't keep the padding.
      root.style.removeProperty("--geneo-dock-h");
    };
  }, []);
  const railReplaceRef = useRef(null);
  // <img> inside ReactCrop — needed to translate crop pixels back to natural
  // image coordinates on APPLY (offsetWidth/offsetHeight give layout px;
  // naturalWidth/naturalHeight give source px).
  const cropImgRef = useRef(null);
  const [railDragOver, setRailDragOver] = useState(false);
  // Per-slot drag-drop state for the rail:
  //   • railThumbDragIdx — index of the thumbnail currently being dragged
  //     (null when no swap in progress). Set on dragstart, cleared on end.
  //   • railDropTargetIdx — index of the slot currently under the pointer
  //     during a drag (either a thumb or the "+" slot). Drives the gold-glow
  //     highlight on the active drop target.
  const [railThumbDragIdx, setRailThumbDragIdx] = useState(null);
  const [railDropTargetIdx, setRailDropTargetIdx] = useState(null);
  // Full-size preview modal state — null when closed, otherwise the ref index being previewed.
  const [previewIndex, setPreviewIndex] = useState(null);
  const [rotating, setRotating] = useState(false);
  // Rail Image Modal · edit toolbar state ────────────────────────────────────
  //   • crop         — percentage-based crop from react-image-crop (controlled state).
  //   • completedCrop— pixel crop in the image element's LAYOUT pixels (offsetWidth/Height).
  //                    We convert these to natural-image pixels on APPLY.
  //   • zoom       — 1..4 (CSS transform scales the image visually; crop box stays fixed).
  //   • rotation   — 0..360° flat rotation baked into APPLY (currently no UI trigger).
  //   • cropMode   — when true, the crop overlay (drag/resize handles) is active.
  //   • orbitMode  — when true, the Blender-style 4-view orbit strip replaces the crop UI.
  //                  Crop and orbit are mutually exclusive.
  //   • orbitViews — { generation_id, views: {front,left,back,right: {image_url}} }
  //                  populated by /generate/orbit-views once the user opens the orbit tab.
  //   • orbitIndex — 0..3 pointer into ["front","left","back","right"] for the active slide.
  //   • orbitLoading / orbitError — request lifecycle.
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [cropMode, setCropMode] = useState(false);
  const [orbitMode, setOrbitMode] = useState(false);
  const [processing, setProcessing] = useState(false);
  // 360 Orbit generation state
  const [orbitViews, setOrbitViews] = useState(null); // {generation_id, views:{...}}
  const [orbitIndex, setOrbitIndex] = useState(0);    // 0=front 1=left 2=back 3=right
  const [orbitLoading, setOrbitLoading] = useState(false);
  const [orbitError, setOrbitError] = useState(null);
  const [orbitPinning, setOrbitPinning] = useState(false);
  const [orbitApplying, setOrbitApplying] = useState(false);
  // Drag tracking for the swipeable strip
  const orbitDragRef = useRef(null); // {x, startOffset}
  const [orbitDragOffset, setOrbitDragOffset] = useState(0); // px, live drag delta
  const [modeOpen, setModeOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [novaOpen, setNovaOpen] = useState(false);
  const [novaLoading, setNovaLoading] = useState(false);
  const [novaRewrites, setNovaRewrites] = useState([]);
  const [submodeOpen, setSubmodeOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraActive, setCameraActive] = useState(null); // last-selected preset id, for visual affordance
  const [angleOpen, setAngleOpen] = useState(false);
  const [angleActive, setAngleActive] = useState(null); // last-selected image-angle id
  const [dragHover, setDragHover] = useState(false); // paperclip drop target glow

  const current = MODE_OPTIONS.find((m) => m.key === mode) || MODE_OPTIONS[0];
  const currentSubmode = VIDEO_SUBMODES.find((s) => s.key === videoSubmode) || VIDEO_SUBMODES[0];
  const currentModel = SEEDANCE_MODELS.find((m) => m.key === videoModel) || SEEDANCE_MODELS[0];
  const isVideo = mode === "video";
  const creditCost = computeCreditCost(mode, videoSubmode, duration, videoModel);

  // Close popovers on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setModeOpen(false); setAttachOpen(false); setSkillsOpen(false); setNovaOpen(false); setSubmodeOpen(false); setModelOpen(false); setCameraOpen(false); setAngleOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submit = useCallback(() => {
    if (disabled) return;
    const trimmed = (prompt || "").trim();
    if (!trimmed) { toast.error("Speak your vision first."); return; }
    onSubmit?.({ prompt: trimmed, mode });
  }, [prompt, mode, onSubmit, disabled]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && (e.metaKey || e.ctrlKey)) {
      e.preventDefault(); submit();
    }
  };

  const pickMode = async (opt) => {
    setModeOpen(false);
    if (opt.route) { navigate(opt.route); return; }
    if (opt.action === "openNova") {
      window.dispatchEvent(new CustomEvent("nova:open"));
      return;
    }
    if (opt.action === "rewrites") {
      setNovaOpen(true);
      setNovaLoading(true);
      setNovaRewrites([]);
      try {
        // No `style` passed — Nova rewrites use the user's current pick (if any)
        // from the Pick Your Style tray; forcing afro-egyptian here was the
        // old leak that nudged every rewrite into Egyptian Futura phrasing.
        const { data } = await api.post("/prompt/rewrites", { prompt: prompt || "a hero" });
        setNovaRewrites(data.rewrites || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Nova rewrites unavailable");
        setNovaOpen(false);
      } finally {
        setNovaLoading(false);
      }
      return;
    }
    setMode?.(opt.key);
  };

  const acceptRewrite = (r) => {
    setPrompt?.(r);
    setNovaOpen(false);
    toast.success("Prompt inserted");
  };

  // ── Rail Image Modal helpers ───────────────────────────────────────────────
  // Reset edit state whenever the previewed ref changes (open/swap).
  useEffect(() => {
    setCrop(undefined);
    setCompletedCrop(null);
    setZoom(1);
    setRotation(0);
    setCropMode(false);
    setOrbitMode(false);
    setOrbitViews(null);
    setOrbitIndex(0);
    setOrbitLoading(false);
    setOrbitError(null);
    setOrbitDragOffset(0);
  }, [previewIndex]);
  const previewRef = previewIndex != null ? refs?.[previewIndex] : null;
  const closePreview = () => setPreviewIndex(null);

  // ── Feed drop → rail helper ────────────────────────────────────────────────
  // The GenerationFeed publishes an `application/x-geneo-feed-item` payload on
  // dragstart. When the user drops one on the rail we download the media URL
  // (same-origin via /api/files) and hand it to the standard file-add flow so
  // the parent's upload/normalize logic is untouched.
  //
  // Video drops are gated by mode: the rail is visible in image / video / v2v
  // / turnaround modes, but video *references* only make sense in video and
  // v2v modes. Dropping a video anywhere else politely toasts instead of
  // silently failing at upload time.
  const parseFeedItem = (e) => {
    // Some browsers (Firefox in restricted contexts) throw a security
    // exception when reading a dataTransfer field the drag source didn't
    // set. Wrap the entire read so a failure just means "not a feed drag".
    let raw;
    try { raw = e.dataTransfer.getData("application/x-geneo-feed-item"); }
    catch { return null; }
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };
  const feedItemAcceptedHere = (item) => {
    if (!item) return false;
    if (item.kind === "video" && !(mode === "video" || mode === "v2v")) {
      toast.error("Switch to AI Video / V2V mode to reference a video render.");
      return false;
    }
    return true;
  };
  const fetchFeedItemAsFile = async (item) => {
    // `credentials:'include'` is safe on same-origin /api/files but some
    // engines error when combined with a `data:` URL. Only include it for
    // http(s) URLs where the request might need the session cookie.
    // External fal-CDN URLs are routed through our /api/proxy/media so the
    // fetch stays CORS-clean and can be re-uploaded as a File without taint.
    const src = proxyMediaUrl(item.media_url);
    const isDataUrl = /^data:/i.test(src || "");
    const fetchOpts = isDataUrl ? {} : { credentials: "include" };
    const resp = await fetch(src, fetchOpts);
    if (!resp.ok) throw new Error(`Feed fetch ${resp.status}`);
    const blob = await resp.blob();
    const isVid = item.kind === "video";
    const ext = isVid ? "mp4" : "png";
    const type = blob.type || (isVid ? "video/mp4" : "image/png");
    const idTag = (item.generation_id || item.id || Date.now().toString()).slice(-8);
    return new File([blob], `feed-${idTag}.${ext}`, { type });
  };

  // ── 360 Orbit — generate 4 consistent views via Nano Banana ──────────────
  // Fires when the user first switches into orbit mode. Uses the ref's fal
  // `uploaded_url` (or the local blob URL fallback) as the identity anchor.
  const ORBIT_KEYS = ["front", "left", "back", "right"];
  const ORBIT_LABELS = { front: "FRONT", left: "LEFT", back: "BACK", right: "RIGHT" };
  const generateOrbitViews = async () => {
    if (!previewRef || orbitLoading) return;
    const imageUrl = previewRef.uploaded_url || previewRef.url;
    if (!imageUrl) {
      toast.error("Reference is still uploading — try again in a moment.");
      return;
    }
    setOrbitLoading(true);
    setOrbitError(null);
    try {
      const { data } = await api.post("/generate/orbit-views", {
        image_url: imageUrl,
        prompt: "",
      });
      const views = data?.views || {};
      const gotAny = ORBIT_KEYS.some((k) => views[k]?.image_url);
      if (!gotAny) throw new Error("No views returned");
      setOrbitViews({ generation_id: data.generation_id, views });
      setOrbitIndex(0);
      const failed = (data.failures || []).length;
      if (failed) toast.warning(`Orbit ready · ${4 - failed} of 4 views generated.`);
      else toast.success("360 Orbit ready — swipe or tap Front · Left · Back · Right");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Orbit generation failed";
      setOrbitError(String(msg));
      toast.error(String(msg).slice(0, 140));
    } finally {
      setOrbitLoading(false);
    }
  };

  // Swipeable strip — 1px drag = 1px offset; threshold = 60px to snap to next slide.
  const onOrbitPointerDown = (e) => {
    if (!orbitViews) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    orbitDragRef.current = { x: e.clientX, startOffset: 0 };
  };
  const onOrbitPointerMove = (e) => {
    const d = orbitDragRef.current;
    if (!d) return;
    setOrbitDragOffset(e.clientX - d.x);
  };
  const onOrbitPointerUp = () => {
    if (!orbitDragRef.current) return;
    const dx = orbitDragOffset;
    orbitDragRef.current = null;
    const threshold = 60;
    if (dx <= -threshold && orbitIndex < ORBIT_KEYS.length - 1) setOrbitIndex((i) => i + 1);
    else if (dx >= threshold && orbitIndex > 0) setOrbitIndex((i) => i - 1);
    setOrbitDragOffset(0);
  };

  // Convert the currently-active orbit view into a new File and swap it into
  // the rail. Downloads through /api/files → same-origin so canvas isn't tainted.
  const applyActiveOrbitAsReference = async () => {
    if (!orbitViews || orbitApplying) return;
    const key = ORBIT_KEYS[orbitIndex];
    const url = orbitViews.views?.[key]?.image_url;
    if (!url) { toast.error("This view isn't ready yet."); return; }
    setOrbitApplying(true);
    try {
      // Orbit views are stored in our object storage → same-origin `/api/files/*`
      // (CORS-clean after backend fix). `proxyMediaUrl` is a no-op on same-origin
      // URLs but future-proofs this path if we ever pin external-CDN URLs.
      const r = await fetch(proxyMediaUrl(url), { credentials: "include" });
      if (!r.ok) throw new Error(`fetch ${r.status}`);
      const blob = await r.blob();
      const file = new File([blob], `orbit-${key}-${Date.now()}.png`, { type: blob.type || "image/png" });
      const idxBefore = previewIndex;
      onRemoveRef?.(idxBefore);
      await onAddRef?.(file);
      setPreviewIndex(Math.max(0, (refs?.length || 1) - 1));
      toast.success(`Orbit · ${ORBIT_LABELS[key]} set as new reference`);
    } catch (e) {
      toast.error("Could not set orbit view — image may be cross-origin locked.");
    } finally {
      setOrbitApplying(false);
    }
  };

  // Send the entire 4-view bundle to the user's Cast Library via /character/pin.
  const sendOrbitToCastLibrary = async () => {
    if (!orbitViews?.generation_id || orbitPinning) return;
    setOrbitPinning(true);
    try {
      await api.post("/character/pin", { character_generation_id: orbitViews.generation_id });
      toast.success("Orbit added to Cast Library");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || e?.message || "Pin failed");
      toast.error(String(msg).slice(0, 140));
    } finally {
      setOrbitPinning(false);
    }
  };

  // Turntable video — chain the 4 orbit views into a seamless 360° spin.
  // Costs `ORBIT_TURNTABLE_CREDITS` credits (surfaced on the button label).
  const ORBIT_TURNTABLE_CREDITS = 12;
  // Curated shortlist of style presets — 6 flagships that read well as a
  // rotating character. Full style roster lives in `/lib/styles.js`; we
  // keep this tight so the bar doesn't overflow on mobile.
  const TURNTABLE_STYLE_PRESETS = [
    { id: null,               label: "AUTO",         hint: "Match source style", accent: "#A09B8C" },
    { id: "wakanda-tech",     label: "WAKANDA-TECH", hint: "Vibranium glow",     accent: "#00E5FF" },
    { id: "nuwaupia",         label: "NUWAUPIA",     hint: "Cosmic aura",        accent: "#3B5FE2" },
    { id: "african-anime-2d", label: "2D ANIME",     hint: "Speed-lines",        accent: "#EB5757" },
    { id: "afro-egyptian",    label: "EGYPTIAN",     hint: "Golden glyphs",      accent: "#F2C94C" },
    { id: "cosmic-royalty",   label: "COSMIC",       hint: "Starscape drift",    accent: "#E27D60" },
  ];
  const [orbitTurntableBusy, setOrbitTurntableBusy] = useState(false);
  const [turntableStyle, setTurntableStyle] = useState(null); // preset id or null → auto
  const createOrbitTurntable = async () => {
    if (!orbitViews?.generation_id || orbitTurntableBusy) return;
    // Guardrail — /generate/orbit-turntable also validates server-side, but
    // catching missing views client-side gives a nicer message.
    const missing = ORBIT_KEYS.filter((k) => !orbitViews.views?.[k]?.image_url);
    if (missing.length) { toast.error(`Turntable needs all 4 views — missing: ${missing.join(", ")}`); return; }
    setOrbitTurntableBusy(true);
    const t = toast.loading("Rendering 360° turntable · this can take 60-120s…");
    try {
      const { data } = await api.post("/generate/orbit-turntable", {
        generation_id: orbitViews.generation_id,
        style: turntableStyle,   // null → server falls back to the source gen's style
      });
      toast.dismiss(t);
      toast.success("360° turntable ready — check your feed");
      // Let the parent feed refresh by broadcasting the same event the studio
      // page already listens for. Non-blocking; harmless if no listener.
      try {
        window.dispatchEvent(new CustomEvent("generation:completed", { detail: data }));
      } catch (_) {}
    } catch (e) {
      toast.dismiss(t);
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || e?.message || "Turntable render failed");
      toast.error(String(msg).slice(0, 160));
    } finally {
      setOrbitTurntableBusy(false);
    }
  };
  // Composite crop + rotation into a real File via an offscreen canvas.
  //
  // `completedCrop` from react-image-crop is in the image element's LAYOUT
  // pixel space (offsetWidth × offsetHeight — unaffected by CSS transform).
  // Because we visually zoom the image with `transform: scale(zoom)` but the
  // crop overlay stays anchored to the un-scaled layout box, a crop rect
  // (cx, cy, cw, ch) in layout coords maps to natural-image pixels via:
  //
  //   scaledTopLeft = layoutSize * (1 - zoom) / 2   (scaled image position in layout box)
  //   naturalX      = (cx - scaledTopLeft.x) / zoom * (naturalW / layoutW)
  //   naturalW_out  = cw / zoom * (naturalW / layoutW)
  //
  // We clamp the resulting rect to the natural image bounds so partial
  // out-of-bounds crops (from panning while zoomed) don't sample garbage.
  const applyEdits = async () => {
    if (!previewRef || processing) return;
    const src = previewRef.uploaded_url || previewRef.url;
    if (!src) return;
    const hasRotation = ((rotation % 360) + 360) % 360 !== 0;
    const hasCrop = completedCrop && completedCrop.width > 0 && completedCrop.height > 0;
    if (!hasRotation && !hasCrop) {
      toast("Nothing to apply — adjust crop or rotation first.");
      return;
    }
    setProcessing(true);
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
      const rad = ((rotation % 360) + 360) % 360 * Math.PI / 180;
      const w0 = img.naturalWidth;
      const h0 = img.naturalHeight;
      const sin = Math.abs(Math.sin(rad));
      const cos = Math.abs(Math.cos(rad));
      const rotW = w0 * cos + h0 * sin;
      const rotH = w0 * sin + h0 * cos;
      const rotCanvas = document.createElement("canvas");
      rotCanvas.width = Math.max(1, Math.round(rotW));
      rotCanvas.height = Math.max(1, Math.round(rotH));
      const rctx = rotCanvas.getContext("2d");
      rctx.translate(rotCanvas.width / 2, rotCanvas.height / 2);
      rctx.rotate(rad);
      rctx.drawImage(img, -w0 / 2, -h0 / 2);
      let finalCanvas = rotCanvas;
      if (hasCrop) {
        // Translate layout-pixel crop → natural-pixel crop, compensating for zoom.
        const imgEl = cropImgRef.current;
        const layoutW = imgEl?.offsetWidth || completedCrop.width;
        const layoutH = imgEl?.offsetHeight || completedCrop.height;
        const naturalW = rotCanvas.width;
        const naturalH = rotCanvas.height;
        const scaledOriginX = (layoutW * (1 - zoom)) / 2;
        const scaledOriginY = (layoutH * (1 - zoom)) / 2;
        const factorX = naturalW / (layoutW * zoom);
        const factorY = naturalH / (layoutH * zoom);
        let nx = (completedCrop.x - scaledOriginX) * factorX;
        let ny = (completedCrop.y - scaledOriginY) * factorY;
        let nw = completedCrop.width * factorX;
        let nh = completedCrop.height * factorY;
        // Clamp inside the natural image so we never draw outside the source.
        if (nx < 0) { nw += nx; nx = 0; }
        if (ny < 0) { nh += ny; ny = 0; }
        if (nx + nw > naturalW) nw = naturalW - nx;
        if (ny + nh > naturalH) nh = naturalH - ny;
        nx = Math.max(0, Math.round(nx));
        ny = Math.max(0, Math.round(ny));
        nw = Math.max(1, Math.round(nw));
        nh = Math.max(1, Math.round(nh));
        const cropCanvas = document.createElement("canvas");
        cropCanvas.width = nw;
        cropCanvas.height = nh;
        const cctx = cropCanvas.getContext("2d");
        cctx.drawImage(
          rotCanvas,
          nx, ny, nw, nh,
          0, 0, nw, nh,
        );
        finalCanvas = cropCanvas;
      }
      const blob = await new Promise((res) => finalCanvas.toBlob(res, "image/png"));
      if (!blob) throw new Error("edit produced no blob");
      const file = new File([blob], `edited-${Date.now()}.png`, { type: "image/png" });
      const idxBefore = previewIndex;
      onRemoveRef?.(idxBefore);
      await onAddRef?.(file);
      setPreviewIndex(Math.max(0, (refs?.length || 1) - 1));
      const parts = [];
      if (hasRotation) parts.push(`rotated ${Math.round(rotation)}°`);
      if (hasCrop) parts.push("cropped");
      toast.success(`Reference ${parts.join(" · ")} — set as new source.`);
    } catch (e) {
      toast.error("Edit failed — image may be cross-origin locked");
    } finally {
      setProcessing(false);
    }
  };
  const replacePreview = async (file) => {
    if (!file || previewIndex == null) return;
    onRemoveRef?.(previewIndex);
    await onAddRef?.(file);
    setPreviewIndex(Math.max(0, (refs?.length || 1) - 1));
    toast.success("Reference replaced");
  };
  const removePreview = () => {
    if (previewIndex == null) return;
    onRemoveRef?.(previewIndex);
    closePreview();
  };

  // Auto-fire the 4-view generation the first time orbit mode is entered for
  // a preview. If the user toggles off and back on we don't re-spend credits;
  // they can hit the "Regenerate" chip inside the strip if they want fresh.
  useEffect(() => {
    if (orbitMode && !orbitViews && !orbitLoading && !orbitError && previewRef?.kind !== "video") {
      generateOrbitViews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orbitMode]);

  return (
    <div
      ref={rootRef}
      data-testid="chat-prompt-dock"
      className={`fixed inset-x-0 z-40 pointer-events-none ${compact ? "bottom-3" : "bottom-6"}`}
    >
      {/* Rail Image Modal — full-size preview with Replace / Rotate / Remove.
          Sits above the dock at z-50; overlays the whole viewport. */}
      {previewRef && (
        <div
          data-testid="dock-ref-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0A0A0A]/85 backdrop-blur-md pointer-events-auto"
          onClick={closePreview}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-[min(94vw,720px)] max-h-[86vh] rounded-2xl border border-[#F2C94C]/30 bg-gradient-to-br from-[#0e0b13] via-[#0a0812] to-[#080810] shadow-[0_0_60px_-8px_rgba(242,201,76,0.55)] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-[0.28em]">
                REFERENCE {previewIndex + 1} / {refs.length}
              </div>
              <button
                type="button"
                onClick={closePreview}
                data-testid="dock-ref-modal-close"
                className="w-8 h-8 rounded-full border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center text-white/70 hover:text-white transition-all"
                aria-label="Close preview"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Image body — Cropper by default; a 3D Orbit scene when orbitMode is on.
                Both share the same rotation/pitch/zoom state, so switching modes is
                seamless and APPLY bakes every axis into the final PNG. */}
            <div className="relative bg-[#0A0A0A] flex items-center justify-center" style={{ height: "56vh", minHeight: 320 }}>
              {previewRef.kind === "video" ? (
                <video
                  src={previewRef.uploaded_url || previewRef.url}
                  controls
                  className="max-h-[56vh] max-w-full"
                  data-testid="dock-ref-modal-video"
                />
              ) : orbitMode ? (
                <div
                  data-testid="dock-ref-modal-orbit-scene"
                  className="absolute inset-0 select-none overflow-hidden"
                  style={{
                    background: "radial-gradient(120% 90% at 50% 40%, rgba(0,229,255,0.10), transparent 55%), radial-gradient(90% 60% at 50% 95%, rgba(242,201,76,0.14), transparent 60%), #050308",
                  }}
                >
                  {/* Perspective grid floor — CSS-only, gold cyan accents.
                      Purely decorative — sells the Blender-style orbit stage. */}
                  <div
                    aria-hidden
                    className="absolute inset-x-0 bottom-0"
                    style={{
                      height: "60%",
                      transform: "perspective(1400px) rotateX(72deg) translateZ(-40px)",
                      transformOrigin: "50% 100%",
                      background:
                        "repeating-linear-gradient(0deg, rgba(0,229,255,0.22) 0 1px, transparent 1px 44px)," +
                        "repeating-linear-gradient(90deg, rgba(242,201,76,0.16) 0 1px, transparent 1px 44px)",
                      maskImage: "linear-gradient(to top, black 0%, transparent 90%)",
                      WebkitMaskImage: "linear-gradient(to top, black 0%, transparent 90%)",
                    }}
                  />

                  {/* Loading state — first-time generation of 4 views. */}
                  {orbitLoading && !orbitViews && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10" data-testid="dock-ref-modal-orbit-loading">
                      <div className="grid grid-cols-4 gap-2 w-[80%] max-w-md">
                        {ORBIT_KEYS.map((k) => (
                          <div key={k} className="aspect-[3/4] rounded-lg border border-[#F2C94C]/20 bg-gradient-to-br from-[#0A0A0A] to-[#12101a] overflow-hidden relative">
                            <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#F2C94C]/6 via-[#00E5FF]/6 to-transparent" />
                            <div className="absolute bottom-1 left-1 right-1 font-mono-hy text-[8px] text-[#F2C94C]/60 tracking-widest text-center">{ORBIT_LABELS[k]}</div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 text-[#F2C94C]">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="font-mono-hy text-[10px] tracking-[0.24em]">SUMMONING 360° ORBIT · 4 VIEWS · NANO BANANA</span>
                      </div>
                    </div>
                  )}

                  {/* Error state — retry chip. */}
                  {!orbitLoading && orbitError && !orbitViews && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10 px-8" data-testid="dock-ref-modal-orbit-error">
                      <div className="font-display text-white text-center text-sm max-w-md leading-snug">Orbit failed — {orbitError}</div>
                      <button
                        type="button"
                        onClick={() => { setOrbitError(null); generateOrbitViews(); }}
                        data-testid="dock-ref-modal-orbit-retry"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-mono-hy text-[10px] tracking-[0.18em] border border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C] transition-all"
                      >
                        <RefreshCcw className="w-3.5 h-3.5" /> RETRY
                      </button>
                    </div>
                  )}

                  {/* Swipeable strip — 4 slides, active one centered. Drag left/right or
                      tap Front · Left · Back · Right snap-buttons to change slide. */}
                  {orbitViews && (
                    <div
                      className="absolute inset-0 flex items-center justify-center touch-none cursor-grab active:cursor-grabbing"
                      onPointerDown={onOrbitPointerDown}
                      onPointerMove={onOrbitPointerMove}
                      onPointerUp={onOrbitPointerUp}
                      onPointerCancel={onOrbitPointerUp}
                      data-testid="dock-ref-modal-orbit-strip"
                    >
                      <div
                        className="relative w-full h-full flex items-center"
                        style={{
                          transform: `translateX(calc(${-orbitIndex * 100}% + ${orbitDragOffset}px))`,
                          transition: orbitDragRef.current ? "none" : "transform 380ms cubic-bezier(0.4,0,0.2,1)",
                        }}
                      >
                        {ORBIT_KEYS.map((k, i) => {
                          const url = orbitViews.views?.[k]?.image_url;
                          const isActive = i === orbitIndex;
                          return (
                            <div
                              key={k}
                              className="w-full h-full flex-shrink-0 flex items-center justify-center px-6"
                              data-testid={`dock-ref-modal-orbit-slide-${k}`}
                              style={{ transform: `scale(${isActive ? 1 : 0.86})`, opacity: isActive ? 1 : 0.55, transition: "transform 380ms cubic-bezier(0.4,0,0.2,1), opacity 380ms" }}
                            >
                              {url ? (
                                <img
                                  src={url}
                                  alt={ORBIT_LABELS[k]}
                                  draggable={false}
                                  className="pointer-events-none max-w-full max-h-[46vh] object-contain rounded-xl"
                                  style={{ boxShadow: isActive ? "0 0 60px 8px rgba(0,229,255,0.35), 0 0 120px 12px rgba(242,201,76,0.20)" : "0 0 24px 2px rgba(0,0,0,0.4)" }}
                                />
                              ) : (
                                <div className="w-[60%] aspect-[3/4] rounded-xl border border-[#EB5757]/40 bg-[#0A0A0A]/60 flex items-center justify-center text-center px-4">
                                  <span className="font-mono-hy text-[10px] text-[#EB5757] tracking-widest">{ORBIT_LABELS[k]} · FAILED</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Active-view badge · top-right */}
                  {orbitViews && (
                    <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5">
                      <span
                        data-testid="dock-ref-modal-orbit-badge"
                        className="px-2.5 py-1 rounded-full border border-[#F2C94C]/50 bg-[#0A0A0A]/85 backdrop-blur font-mono-hy text-[10px] text-[#F2C94C] tracking-widest"
                      >
                        {ORBIT_LABELS[ORBIT_KEYS[orbitIndex]]} · {orbitIndex + 1}/4
                      </span>
                      <button
                        type="button"
                        onClick={() => { setOrbitViews(null); setOrbitIndex(0); setOrbitError(null); generateOrbitViews(); }}
                        disabled={orbitLoading}
                        data-testid="dock-ref-modal-orbit-regenerate"
                        title="Regenerate the 360° orbit — costs 4 credits"
                        className="w-7 h-7 rounded-full border border-[#00E5FF]/40 bg-[#0A0A0A]/85 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                      >
                        <RefreshCcw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {/* Snap buttons · Front · Left · Back · Right */}
                  {orbitViews && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2" data-testid="dock-ref-modal-snap-chips">
                      {ORBIT_KEYS.map((k, i) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setOrbitIndex(i)}
                          data-testid={`dock-ref-modal-snap-${k}`}
                          className={`px-3 py-1.5 rounded-full font-mono-hy text-[10px] tracking-[0.2em] border transition-all ${
                            orbitIndex === i
                              ? "border-[#F2C94C] bg-[#F2C94C] text-[#0A0A0A] shadow-[0_0_18px_-4px_rgba(242,201,76,0.85)]"
                              : "border-[#F2C94C]/40 bg-[#0A0A0A]/70 text-[#F2C94C] hover:bg-[#F2C94C]/15 hover:border-[#F2C94C]"
                          }`}
                        >
                          {ORBIT_LABELS[k]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  data-testid="dock-ref-modal-crop-viewport"
                  className="relative w-full h-full flex items-center justify-center overflow-hidden bg-[#0A0A0A]"
                >
                  <ReactCrop
                    crop={cropMode ? crop : undefined}
                    onChange={(_px, pct) => setCrop(pct)}
                    onComplete={(pxCrop) => setCompletedCrop(pxCrop)}
                    disabled={!cropMode}
                    ruleOfThirds={cropMode}
                    keepSelection={cropMode}
                    className="geneo-crop max-h-full"
                    data-testid="dock-ref-modal-crop"
                  >
                    <img
                      ref={cropImgRef}
                      src={previewRef.uploaded_url || previewRef.url}
                      alt="reference"
                      draggable={false}
                      onLoad={() => {
                        if (cropMode && !crop) {
                          setCrop({ unit: "%", x: 10, y: 10, width: 80, height: 80 });
                        }
                      }}
                      style={{
                        maxHeight: "56vh",
                        maxWidth: "100%",
                        display: "block",
                        transform: `scale(${zoom})`,
                        transformOrigin: "center center",
                        transition: "transform 120ms ease-out",
                      }}
                    />
                  </ReactCrop>
                </div>
              )}
              {!previewRef.uploaded_url && (
                <span className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A]/60 pointer-events-none z-10">
                  <Loader2 className="w-6 h-6 text-[#F2C94C] animate-spin" />
                </span>
              )}
            </div>

            {/* Orbit-only sub-toolbar — Create Turntable Video (chains 4 views
                into a seamless 360° spin via Seedance). Only visible when the
                4-view orbit strip has generated so users can't fire an empty batch. */}
            {orbitMode && orbitViews && (
              <div
                className="px-4 py-3 border-t border-white/[0.06] bg-gradient-to-r from-[#F2C94C]/[0.04] via-[#0A0A0A]/50 to-[#00E5FF]/[0.04]"
                data-testid="dock-ref-modal-orbit-turntable-bar"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em]">SEEDANCE TURNTABLE · STYLE</div>
                    <div className="font-body text-[10px] text-white/60 leading-snug">Pick the aesthetic your 360° spin inherits.</div>
                  </div>
                </div>
                {/* Style preset chips — scrolls horizontally on narrow modals. */}
                <div
                  className="flex items-center gap-1.5 overflow-x-auto pb-1.5 -mx-1 px-1"
                  data-testid="dock-ref-modal-orbit-turntable-styles"
                  style={{ scrollbarWidth: "thin" }}
                >
                  {TURNTABLE_STYLE_PRESETS.map((p) => {
                    const isActive = turntableStyle === p.id;
                    return (
                      <button
                        key={p.id || "auto"}
                        type="button"
                        onClick={() => setTurntableStyle(p.id)}
                        data-testid={`dock-ref-modal-orbit-turntable-style-${p.id || "auto"}`}
                        title={p.hint}
                        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full font-mono-hy text-[9px] tracking-[0.18em] border transition-all ${
                          isActive
                            ? "text-[#0A0A0A] shadow-[0_0_14px_-3px]"
                            : "bg-[#0A0A0A]/60 text-white/70 hover:bg-[#0A0A0A]/90 hover:text-white"
                        }`}
                        style={{
                          borderColor: isActive ? p.accent : `${p.accent}55`,
                          background: isActive ? p.accent : undefined,
                          boxShadow: isActive ? `0 0 14px -3px ${p.accent}b3` : undefined,
                        }}
                      >
                        <span
                          aria-hidden
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: isActive ? "#0A0A0A" : p.accent }}
                        />
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-1">
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-[11px] text-white/70 leading-snug">
                      Chain all 4 views into a smooth 360° spin — front → left → back → right → front.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={createOrbitTurntable}
                    disabled={orbitTurntableBusy}
                    data-testid="dock-ref-modal-orbit-turntable"
                    title="Render a seamless 360° turntable video by chaining the 4 orbit views through Seedance"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-mono-hy text-[11px] tracking-[0.18em] bg-gradient-to-r from-[#F2C94C] to-[#F2D06B] text-[#0A0A0A] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-[0_0_24px_-4px_rgba(242,201,76,0.85)] border border-[#F2C94C]/60"
                  >
                    {orbitTurntableBusy ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />
                        RENDERING SPIN…
                      </>
                    ) : (
                      <>
                        <Film className="w-4 h-4" strokeWidth={2.5} />
                        CREATE TURNTABLE VIDEO
                        <span className="font-mono-hy text-[9px] px-1.5 py-0.5 rounded bg-[#0A0A0A]/20 text-[#0A0A0A] border border-[#0A0A0A]/30 tracking-widest">
                          {ORBIT_TURNTABLE_CREDITS} CR
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Edit toolbar — Crop · Zoom · 360 Orbit toggle · (orbit-specific actions when active) */}
            {previewRef.kind !== "video" && (
              <div className="px-4 py-3 border-t border-white/[0.06] bg-[#0A0A0A]/60" data-testid="dock-ref-modal-edit-toolbar">
                <div className="flex items-center gap-3 flex-wrap">
                  {/* CROP toggle */}
                  <button
                    type="button"
                    onClick={() => {
                      setCropMode((v) => {
                        const next = !v;
                        if (next) {
                          setOrbitMode(false);
                          // Seed the crop rect if we haven't yet, so the user sees
                          // the gold-bordered box immediately.
                          setCrop((c) => c || { unit: "%", x: 10, y: 10, width: 80, height: 80 });
                        }
                        return next;
                      });
                    }}
                    data-testid="dock-ref-modal-crop-toggle"
                    disabled={orbitMode}
                    title="Toggle crop rectangle — drag the box to reposition, pull corners or edges to resize"
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-all font-mono-hy text-[10px] tracking-[0.14em] ${
                      cropMode
                        ? "border-[#F2C94C] bg-[#F2C94C]/15 text-[#F2C94C] shadow-[0_0_18px_-4px_rgba(242,201,76,0.7)]"
                        : "border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C]"
                    } ${orbitMode ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <Crop className="w-3.5 h-3.5" /> CROP
                  </button>

                  {/* ZOOM slider — hidden when orbiting (orbit shows 4 static views). */}
                  {!orbitMode && (
                    <div className="flex items-center gap-2 flex-1 min-w-[180px] max-w-[280px]" data-testid="dock-ref-modal-zoom-wrap">
                      <ZoomIn className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0" />
                      <input
                        type="range"
                        min={1}
                        max={4}
                        step={0.05}
                        value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                        data-testid="dock-ref-modal-zoom"
                        aria-label="Zoom"
                        className="flex-1 accent-[#00E5FF] h-1"
                      />
                      <span className="font-mono-hy text-[9px] text-[#00E5FF] tracking-widest w-8 text-right">{zoom.toFixed(1)}×</span>
                    </div>
                  )}

                  {/* 360 ORBIT toggle — Blender-style multi-angle generation.
                      Uses fal.ai Nano Banana to synthesise front/left/back/right
                      views from this single reference image. Costs 4 credits. */}
                  <button
                    type="button"
                    onClick={() => { setOrbitMode((v) => !v); if (!orbitMode) setCropMode(false); }}
                    data-testid="dock-ref-modal-orbit-toggle"
                    title="360 Orbit — generate 4 consistent views (Front · Left · Back · Right). Costs 4 credits."
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-all font-mono-hy text-[10px] tracking-[0.14em] ${
                      orbitMode
                        ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#00E5FF] shadow-[0_0_18px_-4px_rgba(0,229,255,0.7)]"
                        : "border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF]"
                    }`}
                  >
                    <RefreshCcw className="w-3.5 h-3.5" /> 360° ORBIT
                    {!orbitViews && !orbitMode && (
                      <span className="font-mono-hy text-[9px] px-1.5 py-0.5 rounded bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/40 tracking-widest">4 CR</span>
                    )}
                  </button>

                  {/* Right-side actions — vary by mode.
                      · Orbit mode : Set-as-Reference + Send-to-Cast-Library for the active view
                      · Crop mode  : APPLY bakes crop/rotation into a new File */}
                  {orbitMode ? (
                    <div className="ml-auto flex items-center gap-2">
                      <button
                        type="button"
                        onClick={applyActiveOrbitAsReference}
                        disabled={!orbitViews || orbitApplying}
                        data-testid="dock-ref-modal-orbit-set-ref"
                        title="Replace this rail image with the currently-selected orbit view"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full font-mono-hy text-[10px] tracking-[0.14em] border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        {orbitApplying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                        SET AS REF
                      </button>
                      <button
                        type="button"
                        onClick={sendOrbitToCastLibrary}
                        disabled={!orbitViews || orbitPinning}
                        data-testid="dock-ref-modal-orbit-pin"
                        title="Save this 4-view orbit as a Cast Library character"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full font-mono-hy text-[10px] tracking-[0.14em] bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-[0_0_18px_-4px_rgba(242,201,76,0.7)]"
                      >
                        {orbitPinning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PinIcon className="w-3.5 h-3.5" />}
                        SEND TO CAST
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={applyEdits}
                      disabled={processing}
                      data-testid="dock-ref-modal-apply"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full font-mono-hy text-[10px] tracking-[0.14em] bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-[0_0_18px_-4px_rgba(242,201,76,0.7)]"
                    >
                      {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                      APPLY
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Action row — Swap · Replace · Remove (flat 90° rotate replaced by the
                360° control in the edit toolbar above) */}
            <div className="grid grid-cols-3 gap-2 px-4 py-3 border-t border-white/[0.06]">
              {/* Swap = jump to another rail slot (previous/next). Cycles through refs. */}
              <button
                type="button"
                onClick={() => setPreviewIndex((prev) => (prev + 1) % refs.length)}
                disabled={refs.length < 2}
                data-testid="dock-ref-modal-swap"
                title="Swap to next reference in the rail"
                className="font-mono-hy text-[10px] px-3 py-2.5 rounded-lg border border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-1.5"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> SWAP
              </button>
              {/* Replace = pick a new file to overwrite this ref */}
              <button
                type="button"
                onClick={() => railReplaceRef.current?.click()}
                data-testid="dock-ref-modal-replace"
                title="Pick a new file to replace this reference"
                disabled={processing}
                className="font-mono-hy text-[10px] px-3 py-2.5 rounded-lg border border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" /> REPLACE
              </button>
              {/* Remove = delete this ref from the rail */}
              <button
                type="button"
                onClick={removePreview}
                data-testid="dock-ref-modal-remove"
                title="Remove this reference"
                className="font-mono-hy text-[10px] px-3 py-2.5 rounded-lg border border-[#EB5757]/40 text-[#EB5757] hover:bg-[#EB5757]/10 hover:border-[#EB5757] transition-all flex items-center justify-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> REMOVE
              </button>
            </div>
            <input
              ref={railReplaceRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              data-testid="dock-ref-modal-replace-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) replacePreview(f);
                e.target.value = "";
              }}
            />
          </div>
        </div>
      )}
      <div className="mx-auto max-w-4xl px-3 sm:px-5 pointer-events-auto">
        {/* Popovers rise above the dock */}
        {attachOpen && attachContent && (
          <div className="flex justify-end mb-2" data-testid="dock-attach-popover-wrap">
            <div className="w-full max-w-sm">
              <PopoverCard title="ATTACH · References & Frames" accent="cyan" onClose={() => setAttachOpen(false)} testId="dock-attach-popover" compact>
                {attachContent}
              </PopoverCard>
            </div>
          </div>
        )}
        {skillsOpen && skillsContent && (
          <PopoverCard title="SKILLS · Style · Aspect · Motion" accent="gold" onClose={() => setSkillsOpen(false)} testId="dock-skills-popover">
            {skillsContent}
          </PopoverCard>
        )}
        {novaOpen && (
          <PopoverCard title="NOVA · Prompt Rewrites" accent="gold" onClose={() => setNovaOpen(false)} testId="dock-nova-popover">
            {novaLoading ? (
              <div className="flex items-center justify-center py-8 gap-3 text-[#F2C94C]">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="font-mono-hy text-[10px] tracking-widest">Nova is thinking…</span>
              </div>
            ) : (
              <div className="space-y-2" data-testid="nova-rewrites-list">
                {novaRewrites.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => acceptRewrite(r)}
                    data-testid={`nova-rewrite-${i}`}
                    className="w-full text-left p-4 rounded-2xl bg-[#0A0A0A]/70 border border-[#F2C94C]/15 hover:border-[#F2C94C] hover:shadow-[0_0_24px_-6px_rgba(242,201,76,0.5)] transition-all group"
                  >
                    <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-widest mb-1.5 flex items-center gap-1.5">
                      <Wand2 className="w-2.5 h-2.5" /> REWRITE {i + 1}
                    </div>
                    <div className="font-body text-[13px] text-white leading-relaxed">{r}</div>
                  </button>
                ))}
              </div>
            )}
          </PopoverCard>
        )}

        {/* Mode dropdown floats above-left */}
        {modeOpen && (
          <div className="mb-2 relative">
            <div className="absolute left-2 bottom-0 w-[280px] rounded-2xl border border-white/[0.08] bg-gradient-to-br from-[#0e0b13]/95 via-[#0a0812]/95 to-[#080810]/95 backdrop-blur-xl shadow-[0_0_44px_-12px_rgba(242,201,76,0.35)] overflow-hidden" data-testid="dock-mode-menu">
              {MODE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = opt.key === mode;
                return (
                  <button
                    key={opt.key}
                    onClick={() => pickMode(opt)}
                    data-testid={`dock-mode-option-${opt.key}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all border-b border-white/[0.04] last:border-b-0 ${active ? "bg-[#F2C94C]/8" : "hover:bg-white/[0.04]"}`}
                  >
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${active ? "border-[#F2C94C] bg-[#F2C94C]/10 shadow-[0_0_18px_-4px_rgba(242,201,76,0.6)]" : "border-white/[0.08]"}`}>
                      <Icon className={`w-4 h-4 ${active ? "text-[#F2C94C]" : "text-[#00E5FF]"}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-sm text-white">{opt.label}</div>
                      <div className="font-mono-hy text-[9px] text-white">{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Video submode dropdown — floats above the prompt when AI Video is selected */}
        {isVideo && submodeOpen && (
          <div className="mb-2 relative">
            <div className="absolute left-2 bottom-0 w-[340px] rounded-2xl border border-white/[0.08] bg-gradient-to-br from-[#0e0b13]/95 via-[#0a0812]/95 to-[#080810]/95 backdrop-blur-xl shadow-[0_0_44px_-12px_rgba(0,229,255,0.35)] overflow-hidden" data-testid="dock-videosub-menu">
              <div className="px-4 py-2.5 border-b border-white/[0.06] font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.24em] flex items-center gap-1.5">
                <Film className="w-3 h-3" /> VIDEO MODE
              </div>
              {VIDEO_SUBMODES.map((opt) => {
                const Icon = opt.icon;
                const active = opt.key === videoSubmode;
                return (
                  <button
                    key={opt.key}
                    onClick={() => { setVideoSubmode?.(opt.key); setSubmodeOpen(false); }}
                    data-testid={`dock-videosub-option-${opt.key}`}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-all border-b border-white/[0.04] last:border-b-0 ${active ? "bg-[#00E5FF]/8" : "hover:bg-white/[0.04]"}`}
                  >
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 ${active ? "border-[#00E5FF] bg-[#00E5FF]/10 shadow-[0_0_18px_-4px_rgba(0,229,255,0.6)]" : "border-white/[0.08]"}`}>
                      <Icon className={`w-4 h-4 ${active ? "text-[#00E5FF]" : "text-[#F2C94C]"}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <div className="font-display text-sm text-white">{opt.label}</div>
                        {opt.beta && (
                          <span className="font-mono-hy text-[8px] px-1.5 py-0.5 rounded bg-[#F2C94C]/15 text-[#F2C94C] border border-[#F2C94C]/30 tracking-widest">BETA</span>
                        )}
                      </div>
                      <div className="font-mono-hy text-[9px] text-white leading-relaxed mt-0.5">{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Seedance Model dropdown — video-only, sits above the prompt when Model pill is tapped */}
        {isVideo && modelOpen && (
          <div className="mb-2 relative">
            <div className="absolute left-2 bottom-0 w-[320px] rounded-2xl border border-white/[0.08] bg-gradient-to-br from-[#0e0b13]/95 via-[#0a0812]/95 to-[#080810]/95 backdrop-blur-xl shadow-[0_0_44px_-12px_rgba(242,201,76,0.35)] overflow-hidden" data-testid="dock-model-menu">
              <div className="px-4 py-2.5 border-b border-white/[0.06] font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em] flex items-center gap-1.5">
                <Film className="w-3 h-3" /> SEEDANCE MODEL
              </div>
              {SEEDANCE_MODELS.map((opt) => {
                const active = opt.key === videoModel;
                return (
                  <button
                    key={opt.key}
                    onClick={() => { setVideoModel?.(opt.key); setModelOpen(false); }}
                    data-testid={`dock-model-option-${opt.key}`}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-all border-b border-white/[0.04] last:border-b-0 ${active ? "bg-[#F2C94C]/10" : "hover:bg-white/[0.04]"}`}
                  >
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 ${active ? "border-[#F2C94C] bg-[#F2C94C]/10 shadow-[0_0_18px_-4px_rgba(242,201,76,0.6)]" : "border-white/[0.08]"}`}>
                      <Film className={`w-4 h-4 ${active ? "text-[#F2C94C]" : "text-[#00E5FF]"}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <div className="font-display text-sm text-white">{opt.label}</div>
                        {opt.flatCost && (
                          <span className="font-mono-hy text-[8px] px-1.5 py-0.5 rounded bg-[#F2C94C]/15 text-[#F2C94C] border border-[#F2C94C]/30 tracking-widest">{opt.flatCost}C</span>
                        )}
                      </div>
                      <div className="font-mono-hy text-[9px] text-white leading-relaxed mt-0.5">{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Camera Presets popover — video-only, glassmorphic gold-accent menu */}
        {isVideo && (
          <CameraPresetsMenu
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            activeId={cameraActive}
            onSelect={(preset) => {
              // Append the camera instruction cleanly to the current prompt
              const suffix = ` · Camera: ${preset.instruction}.`;
              const base = (prompt || "").trimEnd();
              const next = base && !base.endsWith(".") ? `${base}.${suffix}` : `${base}${suffix}`;
              setPrompt?.(next.trim());
              setCameraActive(preset.id);
              onCameraChange?.(preset);
              toast.success(`Camera move added — ${preset.name}`, { duration: 1600 });
            }}
          />
        )}

        {/* Camera Angle popover — image-only, cyan-accent counterpart to Camera Presets */}
        {!isVideo && (
          <CameraAnglePresetsMenu
            open={angleOpen}
            onClose={() => setAngleOpen(false)}
            activeId={angleActive}
            onSelect={(preset) => {
              const suffix = ` · Angle: ${preset.instruction}.`;
              const base = (prompt || "").trimEnd();
              const next = base && !base.endsWith(".") ? `${base}.${suffix}` : `${base}${suffix}`;
              setPrompt?.(next.trim());
              setAngleActive(preset.id);
              onAngleChange?.(preset);
              toast.success(`Camera angle added — ${preset.name}`, { duration: 1600 });
            }}
          />
        )}

        {/* Main dock surface */}
        <div className="relative rounded-3xl border border-white/[0.08] bg-gradient-to-br from-[#0e0b13]/95 via-[#0a0812]/95 to-[#080810]/95 backdrop-blur-2xl shadow-[0_0_48px_-16px_rgba(242,201,76,0.35),0_0_48px_-16px_rgba(0,229,255,0.28)] overflow-hidden">
          {/* Aurora rim */}
          <span aria-hidden className="pointer-events-none absolute inset-0 opacity-70"
            style={{ background: "radial-gradient(120% 60% at 0% 0%, rgba(242,201,76,0.06), transparent 55%), radial-gradient(120% 60% at 100% 100%, rgba(0,229,255,0.06), transparent 55%)" }} />
          {/* Sheen */}
          <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(242,201,76,0.5), rgba(0,229,255,0.5), transparent)" }} />

          {/* Textarea + Left Thumbnail Rail (primary upload zone).
              The rail sits to the LEFT of the input and holds up to `refsCap` (default 5)
              image cards. Users can:
                · Click any + card to open the file picker (railFileRef)
                · Drag & drop one or more images anywhere on the rail
                · Click each thumb's X to remove it individually
              The paperclip pill in the toolbar remains as a secondary trigger. */}
          <div className="relative px-4 pt-3 pb-2 flex items-start gap-3">
            {/* Left rail — always visible in modes that accept refs */}
            {(onAddRef && (mode === "image" || mode === "video" || mode === "v2v" || mode === "turnaround")) && (
              <div
                data-testid="dock-ref-rail"
                onDragOver={(e) => {
                  // Only accept EXTERNAL file drags at the rail level.
                  // Internal thumb-swaps have their own per-slot handler
                  // and must not trigger the rail-wide highlight, which
                  // would otherwise pulse the "+" slot's cyan glow every
                  // time the pointer momentarily left a thumb mid-swap.
                  if (railThumbDragIdx !== null) return;
                  const types = Array.from(e.dataTransfer.types || []);
                  // Accept either OS files OR a feed item drag (custom MIME
                  // set by GenerationFeed on drag start).
                  const hasFile = types.includes("Files");
                  const hasFeed = types.includes("application/x-geneo-feed-item");
                  if (hasFile || hasFeed) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                    setRailDragOver(true);
                  }
                }}
                onDragLeave={() => setRailDragOver(false)}
                onDrop={async (e) => {
                  setRailDragOver(false);
                  // Priority order: OS files first (concrete bytes are cheaper
                  // than a network round-trip for the feed URL); then feed item.
                  const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith("image/") || f.type.startsWith("video/"));
                  if (files.length) {
                    e.preventDefault();
                    const slots = Math.max(0, refsCap - (refs?.length || 0));
                    files.slice(0, slots).forEach((f) => onAddRef?.(f));
                    return;
                  }
                  const feedItem = parseFeedItem(e);
                  if (feedItem) {
                    e.preventDefault();
                    if (!feedItemAcceptedHere(feedItem)) return;
                    if ((refs?.length || 0) >= refsCap) { toast.error(`References are capped at ${refsCap}.`); return; }
                    try {
                      const file = await fetchFeedItemAsFile(feedItem);
                      await onAddRef?.(file);
                    } catch (err) {
                      toast.error("Could not attach that render — try again.");
                    }
                  }
                }}
                className={`flex items-center gap-1.5 flex-shrink-0 transition-all ${railDragOver ? "scale-[1.02]" : ""}`}
              >
                {(refs || []).slice(0, refsCap).map((r, i) => {
                  const isDragging = railThumbDragIdx === i;
                  const isDropTarget = railDropTargetIdx === i && railThumbDragIdx !== i;
                  return (
                    <div
                      key={r.url || r.uploaded_url || i}
                      // HTML5 drag-drop wiring — drag any thumb onto another
                      // to swap positions, or drop an external file onto a
                      // thumb to replace that slot's contents.
                      draggable={!!r.uploaded_url}
                      onDragStart={(e) => {
                        if (!r.uploaded_url) { e.preventDefault(); return; }
                        setRailThumbDragIdx(i);
                        try {
                          e.dataTransfer.effectAllowed = "move";
                          // Give the drag a real payload so browsers accept it
                          // even in strict mode; content doesn't matter — the
                          // real coordination happens through React state.
                          e.dataTransfer.setData("application/x-geneo-ref", String(i));
                        } catch (_) {}
                      }}
                      onDragEnd={() => { setRailThumbDragIdx(null); setRailDropTargetIdx(null); }}
                      onDragOver={(e) => {
                        // Accept: (a) thumb-swap (internal), (b) external
                        // OS file drops, (c) feed-item drags. All three
                        // route through this per-slot handler so the gold
                        // glow lights up predictably.
                        const types = Array.from(e.dataTransfer.types || []);
                        const hasFile = types.includes("Files");
                        const hasFeed = types.includes("application/x-geneo-feed-item");
                        const hasInternal = railThumbDragIdx !== null;
                        if (hasFile || hasFeed || hasInternal) {
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = hasInternal ? "move" : "copy";
                          setRailDropTargetIdx(i);
                        }
                      }}
                      onDragLeave={(e) => {
                        // Only clear if pointer actually left this element
                        // (dragleave fires on child transitions too).
                        if (e.currentTarget.contains(e.relatedTarget)) return;
                        setRailDropTargetIdx((cur) => (cur === i ? null : cur));
                      }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setRailDropTargetIdx(null);
                        // 1) External OS files → replace this slot
                        const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith("image/") || f.type.startsWith("video/"));
                        if (files.length && onReplaceRef) {
                          onReplaceRef(i, files[0]);
                          return;
                        }
                        // 2) Feed item → download → replace this slot
                        const feedItem = parseFeedItem(e);
                        if (feedItem) {
                          if (!feedItemAcceptedHere(feedItem)) return;
                          try {
                            const file = await fetchFeedItemAsFile(feedItem);
                            if (onReplaceRef) onReplaceRef(i, file);
                            else await onAddRef?.(file);
                          } catch (err) {
                            toast.error("Could not attach that render — try again.");
                          }
                          return;
                        }
                        // 3) Internal thumb → swap two slots
                        if (railThumbDragIdx !== null && railThumbDragIdx !== i && onReorderRefs) {
                          onReorderRefs(railThumbDragIdx, i);
                        }
                        setRailThumbDragIdx(null);
                      }}
                      className={`relative w-11 h-11 rounded-lg overflow-hidden border transition-all group ${
                        isDropTarget
                          ? "border-[#F2C94C] bg-[#F2C94C]/10 shadow-[0_0_22px_-2px_rgba(242,201,76,0.95)] scale-[1.08]"
                          : isDragging
                            ? "border-[#F2C94C]/40 opacity-40 scale-95"
                            : "border-[#F2C94C]/30 bg-[#0A0A0A] hover:border-[#F2C94C] hover:shadow-[0_0_14px_-4px_rgba(242,201,76,0.7)]"
                      } ${r.uploaded_url ? "cursor-grab active:cursor-grabbing" : "cursor-wait"}`}
                      data-testid={`dock-ref-thumb-${i}`}
                      title={r.uploaded_url ? "Tap to preview · drag to swap slots · drop a file here to replace" : "Uploading…"}
                    >
                      <button
                        type="button"
                        onClick={() => setPreviewIndex(i)}
                        aria-label={`Open reference ${i + 1}`}
                        className="block w-full h-full"
                        // The button itself must NOT be draggable — we want
                        // the DIV to own the drag so a click still fires cleanly.
                        draggable={false}
                        // Suppress the default HTML5 image drag ghost;
                        // browsers auto-drag <img> children which conflicts
                        // with our div-level dragstart.
                        onMouseDown={(e) => { if (e.detail > 1) e.preventDefault(); }}
                      >
                        {r.kind === "video" ? (
                          <video src={r.url || r.uploaded_url} className="w-full h-full object-cover pointer-events-none" muted />
                        ) : (
                          <img src={r.url || r.uploaded_url} alt={`ref-${i}`} draggable={false} className="w-full h-full object-cover pointer-events-none" />
                        )}
                      </button>
                      {!r.uploaded_url && (
                        <span className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A]/60 pointer-events-none">
                          <Loader2 className="w-3.5 h-3.5 text-[#F2C94C] animate-spin" />
                        </span>
                      )}
                      {/* Drag-handle affordance — 4-dot grip in the corner,
                          only visible on hover so it stays clean at rest. */}
                      {r.uploaded_url && (
                        <span
                          aria-hidden
                          className="absolute bottom-0.5 left-0.5 w-3.5 h-3.5 rounded-sm bg-[#0A0A0A]/60 backdrop-blur-sm border border-[#F2C94C]/40 flex items-center justify-center text-[#F2C94C] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                          title="Drag to swap slots"
                        >
                          <GripVertical className="w-2.5 h-2.5" strokeWidth={2.5} />
                        </span>
                      )}
                      <span
                        onClick={(e) => { e.stopPropagation(); onRemoveRef?.(i); }}
                        data-testid={`dock-ref-remove-${i}`}
                        role="button"
                        aria-label={`Remove reference ${i + 1}`}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#0A0A0A] border border-[#EB5757]/60 hover:bg-[#EB5757] flex items-center justify-center text-[#EB5757] hover:text-white opacity-0 group-hover:opacity-100 transition-all cursor-pointer z-10"
                      >
                        <X className="w-2.5 h-2.5" />
                      </span>
                    </div>
                  );
                })}
                {(refs?.length || 0) < refsCap && (
                  <button
                    type="button"
                    onClick={() => railFileRef.current?.click()}
                    data-testid="dock-ref-add"
                    aria-label="Attach reference image"
                    title={refs?.length ? "Add another reference (drag & drop supported)" : "Attach a reference image — drag & drop supported"}
                    className={`w-11 h-11 rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-all ${
                      railDragOver
                        ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF] shadow-[0_0_18px_-4px_rgba(0,229,255,0.85)]"
                        : "border-[#F2C94C]/25 bg-[#0A0A0A] text-[#F2C94C]/60 hover:border-[#F2C94C] hover:bg-[#F2C94C]/[0.06] hover:text-[#F2C94C]"
                    }`}
                  >
                    <Plus className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                )}
                <input
                  ref={railFileRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  data-testid="dock-ref-input"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const slots = Math.max(0, refsCap - (refs?.length || 0));
                    files.slice(0, slots).forEach((f) => onAddRef?.(f));
                    e.target.value = "";
                  }}
                />
              </div>
            )}
            <Textarea
              data-testid="dock-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt?.(e.target.value)}
              onKeyDown={handleKey}
              // AUTO-gated placeholder: when the AUTO toggle is ON, we show
              // the mode-specific example prompt so users get a jumpstart.
              // When OFF, fall back to a neutral prompt — no example text
              // pre-fills the field, which is what the user asked for.
              placeholder={
                auto
                  ? (placeholderByMode?.[mode] || "Speak your vision — Geneo AI will bring it to life…")
                  : "Speak your vision — Geneo AI will bring it to life…"
              }
              // Dim the placeholder so it clearly reads as a suggestion, not
              // real content. Previous `placeholder:text-white` made the
              // example prompt visually identical to typed text.
              className="flex-1 min-w-0 min-h-[80px] max-h-[220px] bg-transparent border-none focus:ring-0 focus-visible:ring-0 font-body text-white placeholder:text-white/40 resize-none px-0"
            />
          </div>

          {/* Controls row */}
          <div className="relative px-3 pb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              {/* MODE trigger */}
              <button
                type="button"
                onClick={() => { setModeOpen((v) => !v); setAttachOpen(false); setSkillsOpen(false); }}
                data-testid="dock-mode-trigger"
                aria-expanded={modeOpen}
                className="group inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[#F2C94C]/30 bg-[#F2C94C]/[0.04] hover:border-[#F2C94C] hover:bg-[#F2C94C]/[0.08] hover:shadow-[0_0_20px_-6px_rgba(242,201,76,0.5)] transition-all"
              >
                <current.icon className="w-4 h-4 text-[#F2C94C]" />
                <span className="font-mono-hy text-[10px] text-white tracking-[0.14em]">{current.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-[#F2C94C]/80 transition-transform ${modeOpen ? "rotate-180" : ""}`} />
              </button>

              {/* Video submode pill — only when AI Video is active */}
              {isVideo && (
                <button
                  type="button"
                  onClick={() => { setSubmodeOpen((v) => !v); setModeOpen(false); setAttachOpen(false); setSkillsOpen(false); setCameraOpen(false); setModelOpen(false); }}
                  data-testid="dock-videosub-trigger"
                  aria-expanded={submodeOpen}
                  title={currentSubmode.desc}
                  className="group inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/[0.04] hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.08] hover:shadow-[0_0_20px_-6px_rgba(0,229,255,0.5)] transition-all"
                >
                  <currentSubmode.icon className="w-4 h-4 text-[#00E5FF]" />
                  <span className="font-mono-hy text-[10px] text-white tracking-[0.14em]">{currentSubmode.label}</span>
                  {currentSubmode.beta && (
                    <span className="font-mono-hy text-[8px] px-1.5 py-0.5 rounded bg-[#F2C94C]/15 text-[#F2C94C] border border-[#F2C94C]/30 tracking-widest">BETA</span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-[#00E5FF]/80 transition-transform ${submodeOpen ? "rotate-180" : ""}`} />
                </button>
              )}

              {/* Seedance Model pill — video-only. Tapping opens the model dropdown. Cost updates live in the credit chip. */}
              {isVideo && (
                <button
                  type="button"
                  onClick={() => { setModelOpen((v) => !v); setSubmodeOpen(false); setModeOpen(false); setAttachOpen(false); setSkillsOpen(false); setCameraOpen(false); }}
                  data-testid="dock-model-trigger"
                  aria-expanded={modelOpen}
                  title={`${currentModel.label} — ${currentModel.desc}`}
                  className={`group inline-flex items-center gap-2 px-3 py-2 rounded-full border transition-all ${
                    currentModel.flatCost
                      ? "border-[#F2C94C] bg-[#F2C94C]/[0.10] shadow-[0_0_20px_-6px_rgba(242,201,76,0.55)]"
                      : "border-[#F2C94C]/30 bg-[#F2C94C]/[0.04] hover:border-[#F2C94C] hover:bg-[#F2C94C]/[0.08] hover:shadow-[0_0_20px_-6px_rgba(242,201,76,0.5)]"
                  }`}
                >
                  <Film className="w-4 h-4 text-[#F2C94C]" />
                  <span className="font-mono-hy text-[10px] text-white tracking-[0.14em]">{currentModel.label}</span>
                  {currentModel.flatCost && (
                    <span className="font-mono-hy text-[8px] px-1.5 py-0.5 rounded bg-[#F2C94C]/20 text-[#F2C94C] border border-[#F2C94C]/40 tracking-widest">{currentModel.flatCost}C</span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-[#F2C94C]/80 transition-transform ${modelOpen ? "rotate-180" : ""}`} />
                </button>
              )}

              {/* Camera Presets pill — video only, sits directly next to Video Submode */}
              {isVideo && (
                <button
                  type="button"
                  onClick={() => { setCameraOpen((v) => !v); setSubmodeOpen(false); setModeOpen(false); setAttachOpen(false); setSkillsOpen(false); }}
                  data-testid="dock-camera-trigger"
                  aria-expanded={cameraOpen}
                  title="Pick a filmmaker-grade camera move — appended to your prompt"
                  className={`group inline-flex items-center gap-2 px-3 py-2 rounded-full border transition-all ${
                    cameraActive
                      ? "border-[#F2C94C] bg-[#F2C94C]/[0.10] shadow-[0_0_20px_-6px_rgba(242,201,76,0.55)]"
                      : "border-[#F2C94C]/30 bg-[#F2C94C]/[0.04] hover:border-[#F2C94C] hover:bg-[#F2C94C]/[0.08] hover:shadow-[0_0_20px_-6px_rgba(242,201,76,0.5)]"
                  }`}
                >
                  <Camera className="w-4 h-4 text-[#F2C94C]" />
                  <span className="font-mono-hy text-[10px] text-white tracking-[0.14em]">
                    {cameraActive
                      ? `Camera · ${CAMERA_PRESETS.find((p) => p.id === cameraActive)?.name || "Move"}`
                      : `Camera Presets · ${CAMERA_PRESETS.length}`}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-[#F2C94C]/80 transition-transform ${cameraOpen ? "rotate-180" : ""}`} />
                </button>
              )}

              {/* Music ON/OFF pill — video only, toggles the Seedance audio
                  directive. OFF strips the background music track while
                  preserving SFX / ambient / foley (prompt-conditioned). */}
              {isVideo && setMusicEnabled && (
                <button
                  type="button"
                  onClick={() => setMusicEnabled?.(!musicEnabled)}
                  data-testid="dock-music-toggle"
                  aria-pressed={musicEnabled}
                  title={musicEnabled
                    ? "Music ON — Seedance generates a native soundtrack (click to turn off)"
                    : "Music OFF — sound effects & ambient only, no background music track (click to turn on)"
                  }
                  className={`group inline-flex items-center gap-2 px-3 py-2 rounded-full border transition-all ${
                    musicEnabled
                      ? "border-[#F2C94C]/35 bg-[#F2C94C]/[0.06] hover:border-[#F2C94C] hover:bg-[#F2C94C]/[0.1] hover:shadow-[0_0_20px_-6px_rgba(242,201,76,0.55)]"
                      : "border-[#EB5757]/50 bg-[#EB5757]/[0.08] hover:border-[#EB5757] hover:bg-[#EB5757]/[0.12] hover:shadow-[0_0_20px_-6px_rgba(235,87,87,0.55)]"
                  }`}
                >
                  {musicEnabled
                    ? <Music className="w-4 h-4 text-[#F2C94C]" />
                    : <VolumeX className="w-4 h-4 text-[#EB5757]" />}
                  <span className={`font-mono-hy text-[10px] tracking-[0.14em] ${musicEnabled ? "text-white" : "text-[#EB5757]"}`}>
                    MUSIC {musicEnabled ? "ON" : "OFF"}
                  </span>
                </button>
              )}

              {/* Aspect pill — always visible; content set from parent */}
              {aspect && setAspect && (
                <AspectPopover
                  aspect={aspect}
                  setAspect={setAspect}
                  options={isVideo ? videoAspects : ["1:1", "3:4", "4:3", "16:9", "9:16"]}
                />
              )}

              {/* Duration pill — video only */}
              {isVideo && duration && setDuration && (
                <DurationPopover
                  duration={duration}
                  setDuration={setDuration}
                  options={durations}
                />
              )}

              {/* Camera Angle pill — image mode only, sits between Aspect and AUTO */}
              {!isVideo && (
                <button
                  type="button"
                  onClick={() => { setAngleOpen((v) => !v); setSubmodeOpen(false); setCameraOpen(false); setModeOpen(false); setAttachOpen(false); setSkillsOpen(false); }}
                  data-testid="dock-angle-trigger"
                  aria-expanded={angleOpen}
                  title="Pick a camera angle — appended to your prompt"
                  className={`group inline-flex items-center gap-2 px-3 py-2 rounded-full border transition-all ${
                    angleActive
                      ? "border-[#00E5FF] bg-[#00E5FF]/[0.10] shadow-[0_0_20px_-6px_rgba(0,229,255,0.55)]"
                      : "border-[#00E5FF]/25 bg-[#00E5FF]/[0.03] hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.08] hover:shadow-[0_0_20px_-6px_rgba(0,229,255,0.5)]"
                  }`}
                >
                  <Camera className="w-4 h-4 text-[#00E5FF]" />
                  <span className="font-mono-hy text-[10px] text-white tracking-[0.14em]">
                    {angleActive
                      ? `Angle · ${CAMERA_ANGLES.find((p) => p.id === angleActive)?.name || "Set"}`
                      : `Camera Angle · ${CAMERA_ANGLES.length}`}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-[#00E5FF]/80 transition-transform ${angleOpen ? "rotate-180" : ""}`} />
                </button>
              )}

              {/* AUTO toggle — ON glows cyan and enables the mode-specific
                  example prompt placeholder; OFF dims the button and swaps
                  the placeholder for a neutral hint (no example autofill). */}
              <button
                type="button"
                onClick={() => setAuto?.(!auto)}
                data-testid="dock-auto-toggle"
                data-auto={auto ? "on" : "off"}
                aria-pressed={!!auto}
                title={auto
                  ? "AUTO is ON — Geneo pre-fills a mode-specific example prompt as placeholder. Click to turn OFF."
                  : "AUTO is OFF — no example prompt shown. Click to turn ON."}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-all font-mono-hy text-[10px] tracking-[0.14em] ${
                  auto
                    ? "border-[#00E5FF] bg-[#00E5FF]/12 text-[#00E5FF] shadow-[0_0_20px_-4px_rgba(0,229,255,0.85)] hover:bg-[#00E5FF]/20"
                    : "border-white/[0.10] bg-white/[0.02] text-white/45 hover:text-white hover:border-[#00E5FF]/40"
                }`}
              >
                <Zap className={`w-3.5 h-3.5 ${auto ? "" : "opacity-60"}`} />
                AUTO
                <span
                  className={`ml-1 px-1.5 py-[1px] rounded-full font-mono-hy text-[8px] tracking-[0.18em] border ${
                    auto
                      ? "border-[#00E5FF]/70 bg-[#00E5FF]/20 text-[#00E5FF]"
                      : "border-white/15 bg-white/[0.03] text-white/50"
                  }`}
                  data-testid="dock-auto-state"
                >
                  {auto ? "ON" : "OFF"}
                </span>
              </button>

              {/* Skills pill */}
              {skillsContent && (
                <button
                  type="button"
                  onClick={() => { setSkillsOpen((v) => !v); setAttachOpen(false); setModeOpen(false); }}
                  data-testid="dock-skills-trigger"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/[0.08] bg-white/[0.02] hover:border-[#F2C94C]/60 hover:text-white hover:shadow-[0_0_20px_-6px_rgba(242,201,76,0.4)] transition-all font-mono-hy text-[10px] tracking-[0.14em] text-white"
                >
                  <Settings2 className="w-3.5 h-3.5" />
                  PICK YOUR STYLE
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Queue status pill — inline variant, right-aligned on the
                  same row as the toolbar chips. Auto-hides when there are
                  no items. Supplied by Studio via the `queueSlot` prop. */}
              {queueSlot}
              {/* Credit cost indicator — shows the upfront cost for the queued generation.
                  Reflects the selected Seedance model (2.5 = 2x) so users see the real price. */}
              {creditCost > 0 && (
                <div
                  data-testid="dock-credit-cost"
                  title={`This generation costs ${creditCost} credit${creditCost === 1 ? "" : "s"}`}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#F2C94C]/25 bg-[#F2C94C]/[0.04] font-mono-hy text-[10px] tracking-[0.14em] text-[#F2C94C]"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  GENERATE · {creditCost} {creditCost === 1 ? "CREDIT" : "CREDITS"}
                </div>
              )}
              {/* Attach (+) */}
              {attachContent && (
                <button
                  type="button"
                  onClick={() => { setAttachOpen((v) => !v); setSkillsOpen(false); setModeOpen(false); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setDragHover(true); }}
                  onDragEnter={(e) => { e.preventDefault(); setDragHover(true); }}
                  onDragLeave={() => setDragHover(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragHover(false);
                    const raw = e.dataTransfer.getData("application/x-geneo-feed-item");
                    if (!raw) return;
                    try {
                      const item = JSON.parse(raw);
                      onFeedDrop?.(item);
                    } catch { /* ignore */ }
                  }}
                  data-testid="dock-attach-trigger"
                  title="Attach references and frames · drop a completed render here to use it as a reference"
                  className={`w-10 h-10 rounded-full border transition-all flex items-center justify-center ${
                    dragHover
                      ? "border-[#F2C94C] bg-[#F2C94C]/[0.15] text-[#F2C94C] shadow-[0_0_28px_-4px_rgba(242,201,76,0.85)] scale-110"
                      : "border-white/[0.08] bg-white/[0.02] text-white hover:border-[#00E5FF]/60 hover:text-[#00E5FF] hover:shadow-[0_0_22px_-6px_rgba(0,229,255,0.55)]"
                  }`}
                >
                  <Paperclip className="w-4 h-4" />
                </button>
              )}
              {/* Send */}
              <button
                type="button"
                onClick={submit}
                disabled={disabled || !(prompt || "").trim()}
                data-testid="dock-send-btn"
                title="Send · queue a generation (⌘/Ctrl + Enter)"
                className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                  (prompt || "").trim() && !disabled
                    ? "bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] shadow-[0_0_28px_-4px_rgba(242,201,76,0.75)] hover:-translate-y-0.5"
                    : "bg-white/[0.05] text-white cursor-not-allowed"
                }`}
              >
                {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Status pill row — refs/frames/AUTO summary — sits OUTSIDE the
            composer surface so it never crowds the prompt textarea. Only
            renders when there's something to show; scrolls horizontally
            on narrow screens so long chip strings don't wrap awkwardly. */}
        {attachSummary && (
          <div
            className="mt-2 px-1 flex items-center gap-2 overflow-x-auto pb-1 justify-end"
            data-testid="dock-attach-summary"
            style={{ scrollbarWidth: "thin" }}
          >
            {attachSummary}
          </div>
        )}

        {/* Hint row */}
        <div className="mt-2 flex items-center justify-center gap-2 font-mono-hy text-[9px] text-white tracking-[0.2em]">
          <Sparkles className="w-2.5 h-2.5 text-[#F2C94C]" /> ⌘ · CTRL + ENTER TO QUEUE · MULTIPLE JOBS RUN IN PARALLEL
        </div>
      </div>
    </div>
  );
}

/* ── PopoverCard ─────────────────────────────────────────────────────────── */
function PopoverCard({ title, accent = "gold", onClose, children, testId, compact = false }) {
  const color = accent === "cyan" ? "text-[#00E5FF]" : "text-[#F2C94C]";
  return (
    <div
      data-testid={testId}
      className={`mb-2 relative rounded-3xl border border-white/[0.08] bg-gradient-to-br from-[#0e0b13]/95 via-[#0a0812]/95 to-[#080810]/95 backdrop-blur-2xl shadow-[0_0_48px_-16px_rgba(242,201,76,0.35),0_0_48px_-16px_rgba(0,229,255,0.28)] overflow-hidden ${compact ? "text-[13px]" : ""}`}
    >
      <div className={`flex items-center justify-between border-b border-white/[0.06] ${compact ? "px-3 py-2" : "px-4 py-3"}`}>
        <div className={`font-mono-hy tracking-[0.24em] ${color} ${compact ? "text-[9px]" : "text-[10px]"}`}>{title}</div>
        <button
          onClick={onClose}
          data-testid={`${testId}-close`}
          className={`rounded-full border border-white/[0.08] hover:border-white/30 flex items-center justify-center text-white hover:text-white transition-all ${compact ? "w-6 h-6" : "w-7 h-7"}`}
        >
          <X className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
        </button>
      </div>
      <div className={`overflow-y-auto ${compact ? "max-h-[52vh] p-3" : "max-h-[65vh] p-4"}`}>
        {children}
      </div>
    </div>
  );
}

export default ChatPromptDock;

/* ── Aspect + Duration inline popovers (dock toolbar chips) ─────────────── */
function AspectPopover({ aspect, setAspect, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="dock-aspect-trigger"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/[0.08] bg-white/[0.02] hover:border-[#00E5FF]/50 hover:text-white transition-all font-mono-hy text-[10px] tracking-[0.14em] text-white"
      >
        <span className="text-[#00E5FF]">▭</span> {aspect}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 left-0 min-w-[160px] rounded-2xl border border-white/[0.08] bg-[#0a0812]/95 backdrop-blur-xl shadow-[0_0_36px_-12px_rgba(0,229,255,0.35)] overflow-hidden z-30" data-testid="dock-aspect-menu">
          {options.map((a) => (
            <button
              key={a}
              onClick={() => { setAspect(a); setOpen(false); }}
              data-testid={`dock-aspect-opt-${a.replace(":", "-")}`}
              className={`w-full flex items-center justify-between gap-3 px-4 py-2 text-left transition-all border-b border-white/[0.04] last:border-b-0 font-mono-hy text-[10px] tracking-widest ${
                aspect === a ? "bg-[#00E5FF]/8 text-[#00E5FF]" : "text-white hover:bg-white/[0.04]"
              }`}
            >
              <span>{a}</span>
              {aspect === a && <span>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DurationPopover({ duration, setDuration, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="dock-duration-trigger"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/[0.08] bg-white/[0.02] hover:border-[#F2C94C]/50 hover:text-white transition-all font-mono-hy text-[10px] tracking-[0.14em] text-white"
      >
        <Clock className="w-3.5 h-3.5 text-[#F2C94C]" /> {duration}S
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 left-0 min-w-[140px] rounded-2xl border border-white/[0.08] bg-[#0a0812]/95 backdrop-blur-xl shadow-[0_0_36px_-12px_rgba(242,201,76,0.35)] overflow-hidden z-30" data-testid="dock-duration-menu">
          {options.map((d) => (
            <button
              key={d}
              onClick={() => { setDuration(d); setOpen(false); }}
              data-testid={`dock-duration-opt-${d}`}
              className={`w-full flex items-center justify-between gap-3 px-4 py-2 text-left transition-all border-b border-white/[0.04] last:border-b-0 font-mono-hy text-[10px] tracking-widest ${
                duration === d ? "bg-[#F2C94C]/8 text-[#F2C94C]" : "text-white hover:bg-white/[0.04]"
              }`}
            >
              <span>{d}S</span>
              {duration === d && <span>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
